import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import {
  ArrowLeft, Navigation, Clock, Play, Square, CheckCircle,
  FileText, Mic, Loader2, AlertTriangle, MapPin,
  Phone, ChevronRight, Shield, Sparkles,
  X, ChevronDown, ChevronUp, User, Heart, ClipboardList
} from "lucide-react";
import CommunicationBar from "../../components/employee/CommunicationBar";
import EmergencyModal from "../../components/employee/EmergencyModal";
import MessageOfficeModal from "../../components/employee/MessageOfficeModal";

// Workflow Step Component
const WorkflowStep = ({ step, currentStep, label }) => {
  const isComplete = currentStep > step;
  const isCurrent = currentStep === step;

  return (
    <div className="flex flex-col items-center">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
          isComplete
            ? "bg-emerald-500 text-white"
            : isCurrent
            ? "bg-[#06372B] text-white"
            : "bg-gray-200 text-gray-500"
        }`}
      >
        {isComplete ? (
          <CheckCircle className="w-4 h-4" />
        ) : (
          step + 1
        )}
      </div>
      <span className={`text-xs mt-1 ${isCurrent ? "font-medium text-[#06372B]" : "text-gray-500"}`}>
        {label}
      </span>
    </div>
  );
};

// Voice Recorder Component
const VoiceRecorder = ({ onTranscription, isRecording, setIsRecording }) => {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach(track => track.stop());
        await processRecording(blob);
      };

      mediaRecorder.start();
      setRecording(true);
      setIsRecording(true);
      setDuration(0);
      
      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    } catch (err) {
      console.error("Failed to start recording:", err);
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const processRecording = async (blob) => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");

      const { data } = await api.post("/caregiver-workspace/voice/transcribe", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { enhance_grammar: true },
      });

      onTranscription({
        raw: data.raw_transcription,
        enhanced: data.enhanced_transcription,
        duration: data.duration_seconds,
      });
    } catch (err) {
      console.error("Transcription failed:", err);
      alert("Transcription failed. Please try typing instead.");
    } finally {
      setProcessing(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex items-center gap-3">
      {processing ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Processing...
        </div>
      ) : recording ? (
        <>
          <div className="flex items-center gap-2 text-sm text-red-600">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Recording {formatDuration(duration)}
          </div>
          <button
            onClick={stopRecording}
            className="p-3 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
            data-testid="stop-recording-btn"
          >
            <Square className="w-5 h-5" />
          </button>
        </>
      ) : (
        <button
          onClick={startRecording}
          className="p-3 rounded-full bg-[#06372B] text-white hover:bg-[#0a4a3a] transition-colors"
          data-testid="start-recording-btn"
        >
          <Mic className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default function VisitWorkflow() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Workflow state
  const [currentStep, setCurrentStep] = useState(0);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  
  // Documentation state
  const [careNotes, setCareNotes] = useState("");
  const [enhancedNotes, setEnhancedNotes] = useState(null);
  const [showEnhanced, setShowEnhanced] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [savingDocs, setSavingDocs] = useState(false);
  
  // Modal state
  const [showEmergency, setShowEmergency] = useState(false);
  
  // Signature state
  const [signature, setSignature] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const workflowSteps = ["Navigate", "Clock In", "Tasks", "Document", "Sign", "Complete"];

  const loadVisit = useCallback(async () => {
    try {
      setError(null);
      const { data } = await api.get(`/caregiver-workspace/visit/${visitId}`);
      setVisit(data);
      
      // Set current step based on visit status
      const stepMap = {
        scheduled: 0,
        en_route: 1,
        clocked_in: 2,
        in_progress: 2,
        documenting: 3,
        reviewing: 4,
        clocked_out: 5,
        completed: 5,
      };
      setCurrentStep(stepMap[data.visit.status] || 0);
      
      // Load existing documentation
      if (data.documentation?.care_notes_raw) {
        setCareNotes(data.documentation.care_notes_raw);
      }
    } catch (err) {
      console.error("Failed to load visit:", err);
      setError(err.response?.data?.detail || "Failed to load visit");
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    loadVisit();
  }, [loadVisit]);

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  };

  const handleNavigate = async () => {
    try {
      const { data } = await api.post(`/caregiver-workspace/visit/${visitId}/navigate`);
      window.open(data.navigation_url, "_blank");
      setCurrentStep(1);
      await loadVisit();
    } catch (err) {
      console.error("Navigation failed:", err);
    }
  };

  const handleClockIn = async () => {
    setClockingIn(true);
    setGpsError(null);
    
    try {
      const gps = await getCurrentPosition();
      const { data } = await api.post(`/caregiver-workspace/visit/${visitId}/clock-in`, {
        visit_id: visitId,
        gps,
      });
      
      if (data.status === "gps_warning") {
        // Handle GPS warning
        if (confirm(`${data.warning}\n\nProceed anyway?`)) {
          await api.post(`/caregiver-workspace/visit/${visitId}/clock-in`, {
            visit_id: visitId,
            gps,
            override_gps_warning: true,
            override_reason: "Confirmed at correct location",
          });
        } else {
          setClockingIn(false);
          return;
        }
      }
      
      setCurrentStep(2);
      await loadVisit();
    } catch (err) {
      if (err.code === 1) {
        setGpsError("Location access denied. Please enable GPS.");
      } else if (err.code === 2) {
        setGpsError("Location unavailable. Please try again.");
      } else {
        setGpsError(err.response?.data?.detail || "Clock in failed");
      }
    } finally {
      setClockingIn(false);
    }
  };

  const handleSaveDocumentation = async () => {
    setSavingDocs(true);
    try {
      await api.post(`/caregiver-workspace/visit/${visitId}/documentation`, {
        visit_id: visitId,
        care_notes: showEnhanced && enhancedNotes ? enhancedNotes : careNotes,
        completed_tasks: [],
        incomplete_tasks: [],
      });
      setCurrentStep(4);
      await loadVisit();
    } catch (err) {
      console.error("Failed to save documentation:", err);
      alert(err.response?.data?.detail || "Failed to save documentation");
    } finally {
      setSavingDocs(false);
    }
  };

  const handleApproveDocumentation = async () => {
    try {
      // Convert canvas to base64 if signature exists
      let signatureData = null;
      if (canvasRef.current) {
        signatureData = canvasRef.current.toDataURL("image/png");
      }
      
      await api.post(`/caregiver-workspace/visit/${visitId}/documentation/approve`, {
        visit_id: visitId,
        final_notes: showEnhanced && enhancedNotes ? enhancedNotes : careNotes,
        signature_data: signatureData,
      });
      
      setCurrentStep(5);
      await loadVisit();
    } catch (err) {
      console.error("Failed to approve documentation:", err);
      alert(err.response?.data?.detail || "Failed to approve documentation");
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    setGpsError(null);
    
    try {
      const gps = await getCurrentPosition();
      
      // Get signature if available
      let signatureData = null;
      if (canvasRef.current) {
        signatureData = canvasRef.current.toDataURL("image/png");
      }
      
      await api.post(`/caregiver-workspace/visit/${visitId}/clock-out`, {
        visit_id: visitId,
        gps,
        signature_data: signatureData,
      });
      
      navigate("/employee/workspace");
    } catch (err) {
      if (err.code === 1) {
        setGpsError("Location access denied. Please enable GPS.");
      } else {
        setGpsError(err.response?.data?.detail || "Clock out failed");
      }
    } finally {
      setClockingOut(false);
    }
  };

  const handleTranscription = (result) => {
    setCareNotes(prev => prev + (prev ? "\n\n" : "") + result.raw);
    if (result.enhanced) {
      setEnhancedNotes(result.enhanced);
    }
  };

  // Signature canvas handlers
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(
      e.clientX - rect.left || e.touches?.[0]?.clientX - rect.left,
      e.clientY - rect.top || e.touches?.[0]?.clientY - rect.top
    );
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    
    ctx.lineTo(
      e.clientX - rect.left || e.touches?.[0]?.clientX - rect.left,
      e.clientY - rect.top || e.touches?.[0]?.clientY - rect.top
    );
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setSignature(canvasRef.current?.toDataURL());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature(null);
    }
  };

  useEffect(() => {
    if (currentStep >= 4) {
      initCanvas();
    }
  }, [currentStep]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#06372B] animate-spin mx-auto" />
          <div className="text-gray-500 mt-4">Loading visit...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto" />
          <div className="text-gray-900 font-medium mt-4">Error Loading Visit</div>
          <div className="text-gray-500 text-sm mt-1">{error}</div>
          <button
            onClick={loadVisit}
            className="mt-4 px-4 py-2 bg-[#06372B] text-white rounded-lg font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { visit: visitData, client, pre_visit_summary: preVisit, care_plan } = visit || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-[#06372B] text-white px-4 py-4 sticky top-0 z-40">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/employee/workspace")}
              className="p-2 -ml-2 rounded-full hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="font-semibold">{visitData?.client_name || client?.full_name}</div>
              <div className="text-sm text-white/70">
                {visitData?.start_time} - {visitData?.end_time}
              </div>
            </div>
          </div>
          
          {/* Workflow Progress */}
          <div className="mt-4 flex items-center justify-between">
            {workflowSteps.map((label, index) => (
              <React.Fragment key={label}>
                <WorkflowStep
                  step={index}
                  currentStep={currentStep}
                  label={label}
                />
                {index < workflowSteps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-1 ${
                      currentStep > index ? "bg-emerald-400" : "bg-white/20"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* GPS Error */}
        {gpsError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-red-800">Location Error</div>
              <div className="text-sm text-red-700 mt-0.5">{gpsError}</div>
            </div>
          </div>
        )}

        {/* Step Content */}
        {currentStep === 0 && (
          /* Navigate Step */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5">
              <h2 className="font-semibold text-lg mb-4">Pre-Visit Summary</h2>
              
              {preVisit && (
                <div className="space-y-4">
                  {/* Address */}
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium">Address</div>
                      <div className="text-sm text-gray-600">{preVisit.client_address}</div>
                    </div>
                  </div>
                  
                  {/* Phone */}
                  {preVisit.client_phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">Phone</div>
                        <a
                          href={`tel:${preVisit.client_phone}`}
                          className="text-sm text-[#06372B] font-medium"
                        >
                          {preVisit.client_phone}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Alerts */}
                  {preVisit.alerts?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Important Alerts
                      </div>
                      <ul className="text-sm text-amber-700 space-y-1">
                        {preVisit.alerts.map((alert, i) => (
                          <li key={i}>• {alert}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Care Plan Summary */}
                  {preVisit.care_plan_summary?.length > 0 && (
                    <div>
                      <div className="font-medium mb-2">Today&apos;s Care Plan</div>
                      <div className="flex flex-wrap gap-2">
                        {preVisit.care_plan_summary.map((item, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-gray-100 rounded-full text-sm"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Emergency Contacts */}
                  {preVisit.emergency_contacts?.length > 0 && (
                    <div>
                      <div className="font-medium mb-2">Emergency Contacts</div>
                      {preVisit.emergency_contacts.map((contact, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                        >
                          <div>
                            <div className="font-medium text-sm">{contact.name}</div>
                            <div className="text-xs text-gray-500">{contact.relationship}</div>
                          </div>
                          <a
                            href={`tel:${contact.phone}`}
                            className="p-2 rounded-full bg-[#06372B] text-white"
                          >
                            <Phone className="w-4 h-4" />
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <button
                onClick={handleNavigate}
                className="mt-6 w-full py-3 px-4 bg-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                data-testid="navigate-btn"
              >
                <Navigation className="w-5 h-5" />
                Navigate to Client
              </button>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          /* Clock In Step */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 text-center">
              <div className="w-16 h-16 rounded-full bg-[#06372B]/10 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-[#06372B]" />
              </div>
              <h2 className="font-semibold text-lg">Ready to Start?</h2>
              <p className="text-gray-500 mt-1">
                Clock in to begin your visit with {visitData?.client_name}
              </p>
              
              <button
                onClick={handleClockIn}
                disabled={clockingIn}
                className="mt-6 w-full py-4 bg-[#06372B] text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="clock-in-btn"
              >
                {clockingIn ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Getting Location...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Clock In
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          /* Tasks Step */
          <div className="space-y-4">
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-700">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-medium">Visit In Progress</span>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h2 className="font-semibold text-lg mb-4">Care Tasks</h2>
              <p className="text-gray-500 mb-4">
                Complete the care activities for this visit. You can add notes as you go.
              </p>
              
              {/* Care Plan Tasks */}
              {care_plan?.services?.map((service, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl mb-2"
                >
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                  <span>{service.service_name || service.service_key}</span>
                </div>
              ))}
              
              {(!care_plan?.services || care_plan.services.length === 0) && (
                <div className="text-gray-500 text-sm">
                  No specific tasks assigned. Provide care as needed.
                </div>
              )}
              
              <button
                onClick={() => setCurrentStep(3)}
                className="mt-6 w-full py-3 px-4 bg-[#06372B] text-white rounded-xl font-medium"
                data-testid="proceed-to-docs-btn"
              >
                Continue to Documentation
              </button>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          /* Documentation Step */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5">
              <h2 className="font-semibold text-lg mb-4">Care Notes</h2>
              <p className="text-gray-500 text-sm mb-4">
                Document your care activities. Use voice or type.
              </p>
              
              {/* Voice Recorder */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-600">
                  <Mic className="w-4 h-4 inline mr-1" />
                  Tap to record voice note
                </div>
                <VoiceRecorder
                  onTranscription={handleTranscription}
                  isRecording={isRecording}
                  setIsRecording={setIsRecording}
                />
              </div>
              
              {/* Notes Input */}
              <textarea
                value={careNotes}
                onChange={(e) => setCareNotes(e.target.value)}
                placeholder="Describe the care provided today..."
                rows={6}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-[#06372B] focus:ring-1 focus:ring-[#06372B] resize-none"
                data-testid="care-notes-input"
              />
              
              {/* AI Enhanced Toggle */}
              {enhancedNotes && (
                <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                  <div className="flex items-center gap-2 text-purple-700 font-medium mb-2">
                    <Sparkles className="w-4 h-4" />
                    AI Enhanced Notes
                  </div>
                  <p className="text-sm text-purple-600 mb-3">
                    AI has improved grammar and readability. No facts changed.
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showEnhanced}
                      onChange={(e) => setShowEnhanced(e.target.checked)}
                      className="w-4 h-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-purple-700">Use enhanced version</span>
                  </label>
                </div>
              )}
              
              <button
                onClick={handleSaveDocumentation}
                disabled={savingDocs || !careNotes.trim()}
                className="mt-6 w-full py-3 px-4 bg-[#06372B] text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="save-docs-btn"
              >
                {savingDocs ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5" />
                    Save & Continue
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          /* Sign Step */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5">
              <h2 className="font-semibold text-lg mb-4">Review & Sign</h2>
              
              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="text-sm text-gray-600 mb-2">Care Notes:</div>
                <div className="text-sm">
                  {showEnhanced && enhancedNotes ? enhancedNotes : careNotes}
                </div>
              </div>
              
              {/* Signature Pad */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Caregiver Signature
                  </label>
                  <button
                    onClick={clearSignature}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                </div>
                <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white">
                  <canvas
                    ref={canvasRef}
                    width={300}
                    height={150}
                    className="w-full touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    data-testid="signature-canvas"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Sign above to confirm documentation accuracy
                </p>
              </div>
              
              <button
                onClick={handleApproveDocumentation}
                disabled={!signature}
                className="w-full py-3 px-4 bg-[#06372B] text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="approve-btn"
              >
                <CheckCircle className="w-5 h-5" />
                Approve Documentation
              </button>
            </div>
          </div>
        )}

        {currentStep >= 5 && (
          /* Complete Step */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-semibold text-lg">Documentation Complete!</h2>
              <p className="text-gray-500 mt-1">
                Ready to clock out and finish the visit.
              </p>
              
              <button
                onClick={handleClockOut}
                disabled={clockingOut}
                className="mt-6 w-full py-4 bg-red-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="clock-out-btn"
              >
                {clockingOut ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Getting Location...
                  </>
                ) : (
                  <>
                    <Square className="w-5 h-5" />
                    Clock Out
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Emergency FAB */}
        <button
          onClick={() => setShowEmergency(true)}
          className="fixed bottom-24 right-4 w-14 h-14 rounded-full bg-red-600 text-white shadow-lg flex items-center justify-center hover:bg-red-700 transition-colors z-30"
          data-testid="emergency-fab"
        >
          <Shield className="w-6 h-6" />
        </button>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-40">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          <Link
            to="/employee/workspace"
            className="flex flex-col items-center gap-1 py-2 px-3 text-gray-400"
          >
            <Home className="w-5 h-5" />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <div className="flex flex-col items-center gap-1 py-2 px-3 text-[#06372B]">
            <Clock className="w-5 h-5" />
            <span className="text-xs font-medium">Visit</span>
          </div>
        </div>
      </nav>

      {/* Emergency Modal */}
      {showEmergency && (
        <EmergencyModal
          visit={visitData}
          onClose={() => setShowEmergency(false)}
          onSuccess={loadVisit}
        />
      )}
    </div>
  );
}
